import { useEffect, useRef } from 'react'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

interface UseFocusTrapOptions {
  /** Trap is engaged while true (the overlay is open). */
  active: boolean
  /** Where to send focus on deactivate; defaults to the element focused at activation. */
  restoreTo?: HTMLElement | null
}

/**
 * Backs the aria-modal contract for Dialog and Drawer: moves focus into the
 * container on activate, keeps Tab / Shift+Tab cycling inside it, and restores
 * focus to the previously-focused element on deactivate/unmount.
 *
 * Call it before any effect that opens the overlay (e.g. showModal) so the
 * pre-open focus target is captured for restore.
 */
export function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  { active, restoreTo }: UseFocusTrapOptions,
) {
  const restoreToRef = useRef(restoreTo ?? null)

  useEffect(() => {
    restoreToRef.current = restoreTo ?? null
  }, [restoreTo])

  useEffect(() => {
    if (!active) return
    const node = ref.current
    if (!node) return

    const previous =
      document.activeElement instanceof HTMLElement && !node.contains(document.activeElement)
        ? document.activeElement
        : null
    const focusables = () => Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))

    // Initial focus: the container itself when it opts in (tabindex="-1"
    // panels announce their label), else its first control.
    if (!node.contains(document.activeElement)) {
      if (node.hasAttribute('tabindex')) node.focus()
      else (focusables()[0] ?? node).focus()
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) {
        event.preventDefault()
        node.focus()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const current = document.activeElement
      // The container itself is a boundary in BOTH directions: it is the
      // initial focus for tabindex="-1" panels but never part of focusables(),
      // so without this a Shift+Tab from the freshly-opened panel would fall
      // through to the browser default and escape behind the scrim.
      const atBoundary = current === node || !node.contains(current)
      if (event.shiftKey) {
        if (atBoundary || current === first) {
          event.preventDefault()
          last.focus()
        }
      } else if (atBoundary || current === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      ;(restoreToRef.current ?? previous)?.focus()
    }
  }, [ref, active])
}
